import { useState, useEffect } from "react";
import { CheckTaxParameter, CheckResponseError } from "winslow-core";
import { Button, Label, Input, Checkbox } from "../../../ds2/components";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import Modal from "../../shared/Modal";
import toast from "react-hot-toast";
import { toastSuccess } from "../../../config";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";

type ModalTaxParameterEditProps = {
    isOpen: boolean;
    taxParameter: CheckTaxParameter | null;
    onClose: () => void;
    onSave?: (taxParameter: CheckTaxParameter, value: string) => Promise<{ success: boolean; error?: CheckResponseError }>;
};

export const ModalTaxParameterEdit = ({
    isOpen,
    taxParameter,
    onClose,
    onSave,
}: ModalTaxParameterEditProps) => {
    const [taxParameterValue, setTaxParameterValue] = useState<string>("");
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    // Initialize value when tax parameter changes
    useEffect(() => {
        if (taxParameter) {
            // First check for existing setting value
            let defaultValue = taxParameter.setting?.value ?? taxParameter.default_value ?? "";
            
            // If no setting value, check for default in new_company_values
            if (!taxParameter.setting?.value && taxParameter.definitions?.[0]?.properties) {
                const properties = taxParameter.definitions[0].properties as { new_company_values?: { industry_label: string; value: string }[] };
                if (properties.new_company_values && Array.isArray(properties.new_company_values)) {
                    const defaultEntry = properties.new_company_values.find(
                        entry => entry.industry_label?.toLowerCase() === "default"
                    );
                    if (defaultEntry?.value) {
                        defaultValue = defaultEntry.value;
                    }
                }
            }
            
            setTaxParameterValue(String(defaultValue));
            // Clear error when tax parameter changes
            setCheckResponseError(undefined);
            // Debug: log definitions structure
            if (taxParameter.type === "percent") {
                console.log("[ModalTaxParameterEdit] Percent param definitions:", taxParameter.definitions);
            }
        }
    }, [taxParameter]);

    // Clear error when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setCheckResponseError(undefined);
        }
    }, [isOpen]);

    // Handle currency input changes
    const handleCurrencyChange = (value: string) => {
        // Remove any characters that aren't digits or decimal point
        let cleaned = value.replace(/[^\d.]/g, "");
        
        // Only allow one decimal point
        const parts = cleaned.split(".");
        if (parts.length > 2) {
            cleaned = parts[0] + "." + parts.slice(1).join("");
        }
        
        // Limit to 2 decimal places
        if (parts.length === 2 && parts[1].length > 2) {
            cleaned = parts[0] + "." + parts[1].substring(0, 2);
        }
        
        setTaxParameterValue(cleaned);
    };

    // Handle percent input changes
    const handlePercentChange = (value: string) => {
        // Only allow digits, decimal point, and minus sign
        let cleaned = value.replace(/[^\d.\-]/g, "");
        
        // Only allow minus sign at the beginning
        if (cleaned.includes("-")) {
            const minusIndex = cleaned.indexOf("-");
            if (minusIndex > 0) {
                cleaned = cleaned.replace(/-/g, "");
            } else if (minusIndex === 0) {
                // Keep minus at start, remove any other minus signs
                cleaned = "-" + cleaned.replace(/-/g, "");
            }
        }
        
        // Only allow one decimal point
        const parts = cleaned.split(".");
        if (parts.length > 2) {
            cleaned = parts[0] + "." + parts.slice(1).join("");
        }
        
        setTaxParameterValue(cleaned);
    };

    // Check if percent value is valid (within range if min/max exist)
    const isPercentValueValid = (): boolean => {
        if (!taxParameter || taxParameter.type !== "percent") {
            return true;
        }

        // If there are allowed_values, value must be in that list
        const properties = taxParameter.definitions?.[0]?.properties as { allowed_values?: string[] | null; min_value?: string; max_value?: string } | undefined;
        const hasAllowedValues = properties?.allowed_values && Array.isArray(properties.allowed_values) && properties.allowed_values.length > 0;
        
        if (hasAllowedValues) {
            return properties.allowed_values!.includes(taxParameterValue);
        }

        // If no value entered, invalid
        if (!taxParameterValue || taxParameterValue.trim() === "") {
            return false;
        }

        const numValue = parseFloat(taxParameterValue);
        if (isNaN(numValue)) {
            return false;
        }

        // Check min/max range if they exist
        if (properties?.min_value && properties?.max_value) {
            const minValue = parseFloat(properties.min_value);
            const maxValue = parseFloat(properties.max_value);
            return numValue >= minValue && numValue <= maxValue;
        }

        // If no range constraints, any valid number is acceptable
        return true;
    };

    const handleSave = async () => {
        if (!taxParameter) return;
        
        // Clear any previous errors
        setCheckResponseError(undefined);
        
        if (onSave) {
            try {
                const result = await onSave(taxParameter, taxParameterValue);
                if (result.success) {
                    toast("Tax parameter saved successfully", toastSuccess);
                    handleClose();
                } else if (result.error) {
                    setCheckResponseError(result.error);
                }
            } catch (error) {
                console.error("[ModalTaxParameterEdit] Error saving tax parameter:", error);
                setCheckResponseError({
                    status: 500,
                    data: null,
                    errorMessage: "An unexpected error occurred while saving the tax parameter",
                });
            }
        } else {
            // Default behavior if no onSave callback provided
            console.log("[ModalTaxParameterEdit] Saving tax parameter:", taxParameter.name, taxParameterValue);
            toast("Tax parameter saved successfully", toastSuccess);
            handleClose();
        }
    };

    const handleClose = () => {
        setTaxParameterValue("");
        setCheckResponseError(undefined);
        onClose();
    };

    const stringPercentToTwoDecimalPlaces = (value: string) => {
        return parseFloat(value).toFixed(2);
    }

    console.log("[ModalTaxParameterEdit] taxParameter: ", taxParameter);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            style={{ maxWidth: "600px", width: "90%" }}
            escapeToClose={true}
            allowClickout={true}
        >
            {taxParameter && (
                <div style={{ padding: "24px" }}>
                    <h2 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "600" }}>
                        Edit Tax Parameter
                    </h2>
                    
                    <AppCheckErrorMessage checkResponseError={checkResponseError} />
                    
                    <div style={{ marginBottom: "16px" }}>
                        <Label required>
                            {taxParameter.label}
                        </Label>
                        {taxParameter.description && (
                            <div style={{ fontSize: "14px", color: "#666", marginTop: "4px", marginBottom: "12px" }}>
                                {taxParameter.description}
                            </div>
                        )}
                    </div>

                    {/* String type */}
                    {taxParameter.type === "string" && (
                        <div className="app-custom-property-value">
                            <Input
                                type="text"
                                value={taxParameterValue}
                                onChange={(e) => setTaxParameterValue(e.target.value)}
                                style={{ width: "100%", backgroundColor: "#f5f5f5" }}
                            />
                        </div>
                    )}

                    {/* Currency type */}
                    {taxParameter.type === "currency" && (
                        <div className="app-custom-property-value">
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <span style={{ position: "absolute", left: "8px", color: "#666" }}>$</span>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={taxParameterValue}
                                    onChange={(e) => handleCurrencyChange(e.target.value)}
                                    style={{ width: "100%", paddingLeft: "24px" }}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                    )}

                    {/* Boolean type */}
                    {taxParameter.type === "boolean" && (
                        <div className="app-custom-property-value">
                            <Checkbox
                                name={`tax-param-${taxParameter.id}`}
                                choices={["true", "false"]}
                                choice={taxParameterValue === "true" ? "true" : taxParameterValue === "false" ? "false" : undefined}
                                onChange={(choice) => {
                                    if (typeof choice === "string" || choice === undefined) {
                                        setTaxParameterValue(choice || "");
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Percent type */}
                    {taxParameter.type === "percent" && (() => {
                        const properties = taxParameter.definitions?.[0]?.properties as { allowed_values?: string[] | null; min_value?: string; max_value?: string } | undefined;
                        const hasAllowedValues = properties?.allowed_values && Array.isArray(properties.allowed_values) && properties.allowed_values.length > 0;
                        
                        if (hasAllowedValues) {
                            // Render as GenericSelect if allowed_values exist
                            const options: GenericSelectOption[] = properties.allowed_values!.map((val: string) => ({
                                displayName: `${val}%`,
                                value: val,
                            }));
                            
                            const selectedOption = options.find(opt => opt.value === taxParameterValue) || options[0];
                            
                            return (
                                <div className="app-custom-property-value">
                                    <GenericSelect
                                        options={options}
                                        selected={selectedOption}
                                        onChange={(value: string) => setTaxParameterValue(value)}
                                    />
                                </div>
                            );
                        }
                        
                        // Render as Input if no allowed_values
                        return (
                            <div className="app-custom-property-value">
                                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={taxParameterValue}
                                        onChange={(e) => handlePercentChange(e.target.value)}
                                        style={{ width: "100%", paddingRight: "32px" }}
                                        placeholder="0.00"
                                    />
                                    <span style={{ position: "absolute", right: "8px", color: "#666" }}>%</span>
                                </div>
                                {properties && properties.min_value && properties.max_value && (
                                    <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                        Range: {stringPercentToTwoDecimalPlaces(properties.min_value)}% - {stringPercentToTwoDecimalPlaces(properties.max_value)}%
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Select type */}
                    {taxParameter.type === "select" && taxParameter.options && Array.isArray(taxParameter.options) && (() => {
                        const options: GenericSelectOption[] = taxParameter.options.map((opt: { label: string; value: string }) => ({
                            displayName: opt.label,
                            value: opt.value,
                        }));
                        
                        const selectedOption = options.find(opt => opt.value === taxParameterValue) || options[0];
                        
                        return (
                            <div className="app-custom-property-value">
                                <GenericSelect
                                    options={options}
                                    selected={selectedOption}
                                    onChange={(value: string) => setTaxParameterValue(value)}
                                />
                            </div>
                        );
                    })()}

                    {/* Number type (fallback to string input) */}
                    {taxParameter.type === "number" && (
                        <div className="app-custom-property-value">
                            <Input
                                type="number"
                                value={taxParameterValue}
                                onChange={(e) => setTaxParameterValue(e.target.value)}
                                style={{ width: "100%" }}
                            />
                        </div>
                    )}

                    <div style={{ display: "flex", gap: "12px", marginTop: "24px", justifyContent: "flex-end" }}>
                        <Button
                            label="Cancel"
                            variant="secondary"
                            onClick={handleClose}
                        />
                        <Button
                            label="Save"
                            variant="primary"
                            onClick={handleSave}
                            disabled={taxParameter?.type === "percent" && !isPercentValueValid()}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
};

